// ============================================================
// TMR Portal Admin — aceite de convite: define a senha e já entra (/aceitar-convite)
// ============================================================
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Wordmark } from '@tmr/design-system';
import { useClerkInviteAcceptance } from '@tmr/auth';
import { validatePassword } from '@tmr/login-kit';

/** The route the Clerk invitation redirects to. Configured backend-side as `Clerk:InviteRedirectUrl`. */
export const ACCEPT_INVITE_PATH = '/aceitar-convite';

/** True when the browser is sitting on the accept-invitation route. */
export function isAcceptInviteRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.replace(/\/+$/, '') === ACCEPT_INVITE_PATH;
}

/** Clerk appends the invitation ticket to the redirect as this query parameter. */
const TICKET_PARAM = '__clerk_ticket';

const MIN_PASSWORD_LENGTH = 8;

/** Campos que a instância do Clerk pode exigir no cadastro, na língua de quem lê a tela. */
const CLERK_FIELD_LABELS: Record<string, string> = {
  first_name: 'nome',
  last_name: 'sobrenome',
  legal_accepted: 'aceite dos termos de uso',
  password: 'senha',
};

/**
 * Completes a Clerk invitation inside the portal (06 §6.1).
 *
 * This exists because the invitation used to land on Clerk's hosted Account Portal, where the password the
 * person typed never attached to the account: their first sign-in failed and only a password reset let them
 * in. Here the ticket and the password go to Clerk in **one** `signUp.create` call, so the account is
 * created with the password already set — and the session is activated straight away, so there is no
 * second step to get wrong.
 */
export function AcceptInvitePage() {
  const invite = useClerkInviteAcceptance();

  const [ticket, setTicket] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const value = new URLSearchParams(window.location.search).get(TICKET_PARAM);
    setTicket(value && value.trim() ? value : null);
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !ticket) return;

    if (validatePassword(password, MIN_PASSWORD_LENGTH)) {
      setError(`A senha precisa ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirmation) {
      setError('As duas senhas não são iguais.');
      return;
    }
    if (!invite.isLoaded) {
      setError('O serviço de autenticação ainda está carregando. Tente de novo em instantes.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      // Ticket and password go together — splitting them is what left the account without a usable
      // password before. The e-mail comes from the invitation itself and is not ours to send.
      await invite.accept({ ticket, password });
      // Straight into the portal, on a clean URL — the ticket must not linger in the address bar.
      window.location.replace('/');
      return;
    } catch (cause) {
      setError(describeInviteError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyle}>
      <form onSubmit={(event) => void submit(event)} style={cardStyle}>
        <Wordmark />

        {ticket === null ? (
          <>
            <div>
              <h1 style={titleStyle}>Convite inválido</h1>
              <p style={subtitleStyle}>
                Este link não é um convite válido, ou já foi usado. Se você já criou sua senha, entre
                normalmente; se não, peça um convite novo a quem te cadastrou.
              </p>
            </div>
            <a href="/" style={linkStyle}>Ir para o login</a>
          </>
        ) : (
          <>
            <div>
              <h1 style={titleStyle}>Criar seu acesso</h1>
              <p style={subtitleStyle}>
                Defina a senha que você vai usar para entrar no Portal do Administrador. Ao concluir, você
                já entra — não precisa fazer login de novo.
              </p>
            </div>

            {error && (
              <div role="alert" style={errorStyle}>
                {error}
              </div>
            )}

            <label style={labelStyle}>
              Senha
              <input
                type="password"
                required
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Repita a senha
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                style={inputStyle}
              />
            </label>

            {/*
              Onde o Clerk desenha o desafio anti-bot do cadastro. Precisa existir no DOM antes de
              `signUp.create`: sem o elemento, o Clerk cai para um widget invisível que simplesmente
              bloqueia quem ele suspeitar ser bot — sem chance de a pessoa provar o contrário, e sem nada
              na tela que explique a recusa.
            */}
            <div id="clerk-captcha" />

            <button type="submit" disabled={submitting} style={submitStyle}>
              {submitting ? 'Criando acesso…' : 'Criar acesso e entrar'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

/**
 * Turns a Clerk failure into something the person can act on. The password rules belong to the Clerk
 * instance, so a rejected password is reported with Clerk's own message rather than a guess of ours that
 * could contradict it.
 */
function describeInviteError(cause: unknown): string {
  // Os erros do `@tmr/auth` carregam o código na mensagem e não têm o formato do Clerk — por isso vêm
  // primeiro. Um cadastro que parou por exigência da instância é um problema de configuração, e dizer
  // qual campo faltou é o que separa "não deu" de algo que alguém consegue resolver.
  const own = cause instanceof Error ? cause.message : '';
  if (own.startsWith('sign_up_incomplete:')) {
    const missing = own.slice('sign_up_incomplete:'.length).split(',').filter(Boolean);
    const labels = missing.map((field) => CLERK_FIELD_LABELS[field] ?? field);
    return labels.length > 0
      ? `Para criar a conta o Clerk também exige ${labels.join(' e ')}, que esta tela não pede. `
        + 'Avise quem te cadastrou.'
      : 'Não foi possível concluir o cadastro. Avise quem te cadastrou.';
  }

  const clerk = cause as { errors?: Array<{ code?: string; longMessage?: string; message?: string }> } | null;
  const first = clerk?.errors?.[0];

  if (first?.code === 'form_password_pwned' || first?.code === 'form_password_length_too_short'
    || first?.code === 'form_password_not_strong_enough') {
    return first.longMessage ?? first.message ?? 'Escolha uma senha mais forte.';
  }
  if (first?.code === 'client_state_invalid' || first?.code === 'ticket_invalid') {
    return 'Este convite não é mais válido. Peça um convite novo a quem te cadastrou.';
  }
  if (first?.longMessage || first?.message) return first.longMessage ?? first.message!;

  return cause instanceof Error ? cause.message : 'Não foi possível criar o acesso.';
}

const pageStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'var(--cream-100)',
};

const cardStyle = {
  width: 380,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 16,
  padding: 28,
  background: 'var(--cream-50)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-lg)',
};

const titleStyle = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 19,
  letterSpacing: '-0.01em',
  color: 'var(--ink-900)',
};

const subtitleStyle = {
  margin: '6px 0 0',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--fg-muted)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 6,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--ink-900)',
};

const inputStyle = {
  padding: '10px 12px',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--cream-50)',
  color: 'var(--ink-900)',
  outline: 'none',
};

const submitStyle = {
  marginTop: 4,
  padding: '11px 16px',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--cream-50)',
  background: 'var(--forest-900)',
  border: 0,
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const linkStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  color: 'var(--forest-700)',
  textAlign: 'center' as const,
};

const errorStyle = {
  padding: '10px 12px',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  lineHeight: 1.45,
  color: 'var(--ink-900)',
  background: 'var(--cream-100)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-sm)',
};
